import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { partnersApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { FieldShell, TextArea, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { StatusPill } from '@/components/ui/StatusPill'
import { cn, formatDate, humanise } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { CommissionStatus, PartnerCommission, PartnerCoupon, RecordSettlementRequest } from '@/types/api'

import { COMMISSION_STATUS, COUPON_STATUS, REFERRAL_STATUS, endOfDayIso, isoDay, num, pct, usd } from './format'
import { Segmented, TD, TH, THEAD_ROW, TableScroll } from './ui'

const PAGE_SIZE = 25

function Loading() {
  return (
    <div className="space-y-3 p-5">
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  )
}

function Pager({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (page: number) => void }) {
  if (totalPages <= 1) return null
  return (
    <nav aria-label="Pages" className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
      <Button variant="secondary" size="xs" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span className="num text-[12.5px] text-ink-muted">
        {page} / {totalPages}
      </span>
      <Button variant="secondary" size="xs" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </nav>
  )
}

// ---- Coupons ---------------------------------------------------------------------------------
export function CouponsTab({ coupons, maxDiscountPct }: { coupons: PartnerCoupon[]; maxDiscountPct: number }) {
  if (coupons.length === 0) {
    return <EmptyState title="No coupon codes yet" description="The partner makes codes after signing in at partner.jobslide.app." />
  }
  return (
    <TableScroll>
      <table className="w-full min-w-[900px] text-left text-[13.5px]">
        <thead>
          <tr className={THEAD_ROW}>
            <th className={TH}>Code</th>
            <th className={cn(TH, 'text-right')}>Discount</th>
            <th className={cn(TH, 'text-right')}>Partner earns</th>
            <th className={TH}>Applies to</th>
            <th className={TH}>Status</th>
            <th className={cn(TH, 'text-right')}>Redemptions</th>
            <th className={cn(TH, 'text-right')}>Users</th>
            <th className={cn(TH, 'text-right')}>Earned</th>
            <th className={TH}>Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {coupons.map((coupon) => {
            const status = COUPON_STATUS[coupon.status] ?? COUPON_STATUS.draft
            const over = num(coupon.discountPct) > num(maxDiscountPct)
            const expired = coupon.expiresAt && new Date(coupon.expiresAt).getTime() < Date.now()
            return (
              <tr key={coupon.id}>
                <td className={TD}>
                  <span className="num font-semibold text-ink">{coupon.code}</span>
                  {coupon.note && <span className="block max-w-[220px] truncate text-[12px] text-ink-muted">{coupon.note}</span>}
                </td>
                <td className={cn(TD, 'num text-right font-medium', over ? 'text-danger' : 'text-ink')} title={over ? 'Above the current maximum' : undefined}>
                  {pct(coupon.discountPct)}
                </td>
                <td className={cn(TD, 'num text-right text-brand')}>{pct(coupon.commissionPct)}</td>
                <td className={cn(TD, 'text-ink-soft')}>{coupon.duration === 'forever' ? 'Every payment' : 'First payment'}</td>
                <td className={TD}>
                  <StatusPill size="sm" tone={status.tone}>
                    {status.label}
                  </StatusPill>
                  {expired && <span className="ml-1.5 text-[11.5px] text-ink-faint">expired</span>}
                </td>
                <td className={cn(TD, 'num text-right text-ink-soft')}>
                  {num(coupon.redemptions)}
                  {coupon.maxRedemptions != null && <span className="text-ink-faint"> / {coupon.maxRedemptions}</span>}
                </td>
                <td className={cn(TD, 'num text-right')}>{num(coupon.usersOnboarded)}</td>
                <td className={cn(TD, 'num text-right font-medium text-ink')}>{usd(coupon.earnedCents)}</td>
                <td className={cn(TD, 'whitespace-nowrap text-[12.5px] text-ink-muted')}>{formatDate(coupon.createdAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableScroll>
  )
}

// ---- Users onboarded ------------------------------------------------------------------------
export function ReferralsTab({ partnerId }: { partnerId: string }) {
  const [page, setPage] = useState(1)
  const query = useQuery({
    queryKey: ['partner', partnerId, 'referrals', page],
    queryFn: () => partnersApi.referrals(partnerId, { page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })
  if (query.isLoading) return <Loading />
  if (query.isError) return <ErrorState title="Users could not be loaded" onRetry={() => void query.refetch()} />
  const rows = query.data?.rows ?? []
  if (rows.length === 0) return <EmptyState title="Nobody onboarded yet" description="A user shows here after their first paid checkout with one of this partner's codes." />
  return (
    <>
      <TableScroll>
        <table className="w-full min-w-[900px] text-left text-[13.5px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>User</th>
              <th className={TH}>Code</th>
              <th className={TH}>Plan</th>
              <th className={cn(TH, 'text-right')}>Off / earns</th>
              <th className={TH}>Onboarded</th>
              <th className={TH}>Subscription</th>
              <th className={cn(TH, 'text-right')}>Paid</th>
              <th className={cn(TH, 'text-right')}>Earned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row) => {
              const status = REFERRAL_STATUS[row.status] ?? REFERRAL_STATUS.unknown
              return (
                <tr key={row.id}>
                  <td className={TD}>
                    {row.userId ? (
                      <Link to={`/users/${row.userId}`} className="group block min-w-0">
                        <span className="block truncate font-medium text-ink group-hover:underline">{row.userName || row.userEmail}</span>
                        <span className="block truncate text-[12.5px] text-ink-muted">{row.userEmail}</span>
                      </Link>
                    ) : (
                      <>
                        <span className="block truncate font-medium text-ink">{row.userName || row.userEmail}</span>
                        <span className="block truncate text-[12.5px] text-ink-muted">{row.userEmail}</span>
                      </>
                    )}
                  </td>
                  <td className={cn(TD, 'num font-medium text-ink-soft')}>{row.couponCode}</td>
                  <td className={cn(TD, 'text-ink-soft')}>
                    {humanise(row.plan)}
                    <span className="text-ink-faint"> · {humanise(row.billingPeriod)}</span>
                  </td>
                  <td className={cn(TD, 'num whitespace-nowrap text-right')}>
                    {pct(row.discountPct)} <span className="text-ink-faint">/</span> <span className="text-brand">{pct(row.commissionPct)}</span>
                  </td>
                  <td className={cn(TD, 'whitespace-nowrap text-[12.5px] text-ink-muted')}>{formatDate(row.onboardedAt)}</td>
                  <td className={TD}>
                    <StatusPill size="sm" tone={status.tone}>
                      {status.label}
                    </StatusPill>
                  </td>
                  <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.paidCents)}</td>
                  <td className={cn(TD, 'num text-right font-medium text-ink')}>{usd(row.earnedCents)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </TableScroll>
      <Pager page={page} totalPages={query.data?.pageInfo?.totalPages ?? 1} onPage={setPage} />
    </>
  )
}

// ---- Commissions ----------------------------------------------------------------------------
const COMMISSION_FILTERS: Array<{ value: CommissionStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'To settle' },
  { value: 'settled', label: 'Settled' },
  { value: 'void', label: 'Void' },
]

export function CommissionsTab({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<CommissionStatus | ''>('')
  const [page, setPage] = useState(1)
  const [voiding, setVoiding] = useState<PartnerCommission | null>(null)
  const [reason, setReason] = useState('')
  const query = useQuery({
    queryKey: ['partner', partnerId, 'commissions', status, page],
    queryFn: () => partnersApi.commissions(partnerId, { status, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })

  const voidLine = useMutation({
    mutationFn: () => partnersApi.voidCommission(partnerId, voiding!.id, reason.trim()),
    onSuccess: () => {
      toast.success('Commission voided', `${usd(voiding?.commissionCents)} no longer counts as earned.`)
      setVoiding(null)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['partner', partnerId] })
      void queryClient.invalidateQueries({ queryKey: ['partners'] })
    },
    onError: (error) => toast.error('Could not void the line', error instanceof ApiError ? error.message : undefined),
  })

  const rows = query.data?.rows ?? []
  return (
    <>
      <div className="border-b border-line px-5 py-3">
        <Segmented
          label="Status"
          size="sm"
          value={status}
          onChange={(next) => {
            setStatus(next)
            setPage(1)
          }}
          options={COMMISSION_FILTERS}
        />
      </div>
      {query.isLoading ? (
        <Loading />
      ) : query.isError ? (
        <ErrorState title="Commissions could not be loaded" onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={status ? `No ${COMMISSION_FILTERS.find((f) => f.value === status)?.label.toLowerCase()} lines` : 'No commissions yet'} description="A line is earned for each paid invoice a code covers." />
      ) : (
        <div className={cn('transition-opacity', query.isPlaceholderData && 'opacity-60')}>
          <TableScroll>
            <table className="w-full min-w-[920px] text-left text-[13.5px]">
              <thead>
                <tr className={THEAD_ROW}>
                  <th className={TH}>Earned</th>
                  <th className={TH}>User</th>
                  <th className={TH}>Code</th>
                  <th className={cn(TH, 'text-right')}>List price</th>
                  <th className={cn(TH, 'text-right')}>Discount</th>
                  <th className={cn(TH, 'text-right')}>Paid</th>
                  <th className={cn(TH, 'text-right')}>Rate</th>
                  <th className={cn(TH, 'text-right')}>Commission</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((row) => {
                  const meta = COMMISSION_STATUS[row.status] ?? COMMISSION_STATUS.pending
                  return (
                    <tr key={row.id} className={row.status === 'void' ? 'text-ink-faint' : undefined}>
                      <td className={cn(TD, 'whitespace-nowrap text-[12.5px] text-ink-muted')}>{formatDate(row.earnedAt)}</td>
                      <td className={cn(TD, 'max-w-[200px] truncate text-ink-soft')}>
                        {row.userId ? (
                          <Link to={`/users/${row.userId}`} className="hover:underline">
                            {row.userName || row.userEmail}
                          </Link>
                        ) : (
                          row.userName || row.userEmail
                        )}
                      </td>
                      <td className={cn(TD, 'num text-ink-soft')}>{row.couponCode}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.grossCents)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.discountCents)}</td>
                      <td className={cn(TD, 'num text-right text-ink-soft')}>{usd(row.paidCents)}</td>
                      <td className={cn(TD, 'num text-right')}>{pct(row.commissionPct)}</td>
                      <td className={cn(TD, 'num text-right font-semibold', row.status === 'void' ? 'text-ink-faint line-through' : 'text-ink')}>{usd(row.commissionCents)}</td>
                      <td className={TD}>
                        <StatusPill size="sm" tone={meta.tone} title={row.voidReason ?? undefined}>
                          {meta.label}
                        </StatusPill>
                        {row.status === 'void' && row.voidReason && <span className="mt-0.5 block max-w-[180px] truncate text-[11.5px] text-ink-faint">{row.voidReason}</span>}
                      </td>
                      <td className={cn(TD, 'text-right')}>
                        {row.status === 'pending' && (
                          <Button size="xs" variant="ghost" className="text-danger hover:text-danger" onClick={() => setVoiding(row)}>
                            Void
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableScroll>
          <Pager page={page} totalPages={query.data?.pageInfo?.totalPages ?? 1} onPage={setPage} />
        </div>
      )}

      <Modal
        open={!!voiding}
        onClose={() => !voidLine.isPending && setVoiding(null)}
        size="sm"
        title="Void this commission?"
        description={voiding ? `${usd(voiding.commissionCents)} on ${voiding.couponCode}, earned ${formatDate(voiding.earnedAt)}.` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setVoiding(null)} disabled={voidLine.isPending}>
              Cancel
            </Button>
            <Button variant="danger" loading={voidLine.isPending} disabled={reason.trim().length < 3} onClick={() => voidLine.mutate()}>
              Void line
            </Button>
          </>
        }
      >
        <div className="space-y-3 pb-3">
          <p className="text-[13.5px] leading-relaxed text-ink-soft">It stops counting as earned and will not be settled. The partner sees it as void, with your reason.</p>
          <TextArea label="Reason" required appearance="box" rows={2} className="text-[14px]" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. Refunded on 12 Oct" maxLength={500} />
        </div>
      </Modal>
    </>
  )
}

// ---- Settlements ----------------------------------------------------------------------------
const METHODS = ['Bank transfer', 'UPI', 'PayPal', 'Other'] as const

export function SettlementsTab({ partnerId, partnerName, pendingCents }: { partnerId: string; partnerName: string; pendingCents: number }) {
  const [recording, setRecording] = useState(false)
  const query = useQuery({ queryKey: ['partner', partnerId, 'settlements'], queryFn: () => partnersApi.settlements(partnerId) })
  const rows = query.data ?? []
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3">
        <p className="text-[13px] text-ink-muted">
          To be settled now: <span className={cn('num font-semibold', pendingCents > 0 ? 'text-warning' : 'text-ink')}>{usd(pendingCents)}</span>
        </p>
        <Button size="sm" onClick={() => setRecording(true)} disabled={pendingCents <= 0} title={pendingCents <= 0 ? 'Nothing is waiting to be settled' : undefined}>
          Record settlement
        </Button>
      </div>
      {query.isLoading ? (
        <Loading />
      ) : query.isError ? (
        <ErrorState title="Settlements could not be loaded" onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No settlements yet" description="Record one after you pay the partner; it marks every pending commission up to that date as settled." />
      ) : (
        <TableScroll>
          <table className="w-full min-w-[760px] text-left text-[13.5px]">
            <thead>
              <tr className={THEAD_ROW}>
                <th className={TH}>Recorded</th>
                <th className={TH}>Covers up to</th>
                <th className={cn(TH, 'text-right')}>Amount</th>
                <th className={cn(TH, 'text-right')}>Lines</th>
                <th className={TH}>Method</th>
                <th className={TH}>Reference</th>
                <th className={TH}>Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={cn(TD, 'whitespace-nowrap text-ink-soft')}>{formatDate(row.createdAt)}</td>
                  <td className={cn(TD, 'whitespace-nowrap text-[12.5px] text-ink-muted')}>{formatDate(row.periodTo)}</td>
                  <td className={cn(TD, 'num text-right font-semibold text-success')}>{usd(row.amountCents)}</td>
                  <td className={cn(TD, 'num text-right text-ink-soft')}>{num(row.commissionsCount)}</td>
                  <td className={cn(TD, 'text-ink-soft')}>{row.method || '–'}</td>
                  <td className={cn(TD, 'num max-w-[180px] truncate text-[12.5px] text-ink-soft')}>{row.reference || '–'}</td>
                  <td className={cn(TD, 'max-w-[240px] truncate text-[12.5px] text-ink-muted')} title={row.note || undefined}>
                    {row.note || '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <RecordSettlementModal open={recording} onClose={() => setRecording(false)} partnerId={partnerId} partnerName={partnerName} />
    </>
  )
}

/**
 * Pays out what is pending up to a date. The amount and count shown before
 * confirming are summed here from the pending lines; the server settles the
 * same set (all pending earned up to `upTo`) in one transaction.
 */
export function RecordSettlementModal({ open, onClose, partnerId, partnerName }: { open: boolean; onClose: () => void; partnerId: string; partnerName: string }) {
  const queryClient = useQueryClient()
  const [upTo, setUpTo] = useState(isoDay())
  const [method, setMethod] = useState<(typeof METHODS)[number]>('Bank transfer')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [nothing, setNothing] = useState(false)

  const pending = useQuery({
    queryKey: ['partner', partnerId, 'commissions', 'pending', 'all'],
    // The API pages at most 100 lines: read pending lines page by page (up to 2,000) for the preview.
    queryFn: async () => {
      const first = await partnersApi.commissions(partnerId, { status: 'pending', page: 1, pageSize: 100 })
      const rows = [...first.rows]
      const totalPages = Math.min(first.pageInfo?.totalPages ?? 1, 20)
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await partnersApi.commissions(partnerId, { status: 'pending', page, pageSize: 100 })
        rows.push(...next.rows)
      }
      return { ...first, rows }
    },
    enabled: open,
  })
  const preview = useMemo(() => {
    const cutoff = new Date(endOfDayIso(upTo)).getTime()
    const lines = (pending.data?.rows ?? []).filter((row) => row.status === 'pending' && new Date(row.earnedAt).getTime() <= cutoff)
    return { count: lines.length, cents: lines.reduce((sum, row) => sum + num(row.commissionCents), 0) }
  }, [pending.data, upTo])
  const truncated = (pending.data?.pageInfo?.totalItems ?? 0) > (pending.data?.rows.length ?? 0)

  const record = useMutation({
    mutationFn: (body: RecordSettlementRequest) => partnersApi.recordSettlement(partnerId, body),
    onSuccess: (settlement) => {
      toast.success('Settlement recorded', `${usd(settlement?.amountCents ?? preview.cents)} to ${partnerName}, ${settlement?.commissionsCount ?? preview.count} lines.`)
      void queryClient.invalidateQueries({ queryKey: ['partner', partnerId] })
      void queryClient.invalidateQueries({ queryKey: ['partners'] })
      setReference('')
      setNote('')
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'nothing_to_settle') {
        setNothing(true)
        return
      }
      toast.error('Could not record the settlement', error instanceof ApiError ? error.message : undefined)
    },
  })

  const empty = !pending.isLoading && !pending.isError && preview.count === 0
  return (
    <Modal
      open={open}
      onClose={() => !record.isPending && onClose()}
      size="sm"
      title="Record a settlement"
      description={`Marks ${partnerName}'s pending commissions as paid out.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={record.isPending}>
            Cancel
          </Button>
          <Button
            variant="success"
            loading={record.isPending}
            disabled={empty || pending.isLoading}
            onClick={() =>
              record.mutate({
                upTo: endOfDayIso(upTo),
                method,
                ...(reference.trim() && { reference: reference.trim() }),
                ...(note.trim() && { note: note.trim() }),
              })
            }
          >
            {pending.isLoading ? 'Settle' : `Settle ${usd(preview.cents)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4 pb-3">
        <FieldShell label="Up to and including" htmlFor="settle-upto">
          <input
            id="settle-upto"
            type="date"
            value={upTo}
            max={isoDay()}
            onChange={(event) => {
              setUpTo(event.target.value || isoDay())
              setNothing(false)
            }}
            className="field-box h-10 py-0 text-[14px]"
          />
        </FieldShell>

        <div className={cn('rounded-field border px-4 py-3', empty ? 'border-line bg-surface-muted' : 'border-success/30 bg-success/[0.07]')} aria-live="polite">
          {pending.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : pending.isError ? (
            <p className="text-[13px] text-ink-muted">The pending lines could not be read; the server still settles everything pending up to this date.</p>
          ) : empty || nothing ? (
            <p className="text-[13.5px] text-ink-soft">Nothing pending up to {formatDate(endOfDayIso(upTo))}.</p>
          ) : (
            <>
              <p className="text-[12.5px] text-ink-muted">Will be settled</p>
              <p className="mt-0.5 flex items-baseline gap-2">
                <span className="num text-[24px] font-semibold leading-none text-success">{usd(preview.cents)}</span>
                <span className="text-[13px] text-ink-soft">
                  {preview.count} {preview.count === 1 ? 'commission' : 'commissions'}
                </span>
              </p>
              {truncated && <p className="mt-1 text-[12px] text-warning">More than 2,000 pending lines: the total here may be short. The server settles them all.</p>}
            </>
          )}
        </div>

        <FieldShell label="Method">
          <div role="radiogroup" aria-label="Method" className="flex flex-wrap gap-1.5">
            {METHODS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={method === option}
                onClick={() => setMethod(option)}
                className={cn(
                  'h-9 whitespace-nowrap rounded-pill border px-3.5 text-[12.5px] font-medium transition-colors',
                  method === option ? 'border-ink bg-ink text-oncolor' : 'border-line bg-surface text-ink-soft hover:bg-surface-muted',
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </FieldShell>
        <TextField label="Reference" appearance="box" className="text-[14px]" value={reference} onChange={(event) => setReference(event.target.value)} maxLength={120} placeholder="Transaction ID / UTR (optional)" />
        <TextArea label="Note" appearance="box" rows={2} className="text-[14px]" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional" />
      </div>
    </Modal>
  )
}
